import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import KPICard from '../components/KPICard';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useFilters } from '../hooks/useFilters';
import { printDevis } from '../utils/printDocument';

const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const today = () => new Date().toISOString().split('T')[0];
const inDays = (n) => new Date(Date.now() + n * 86400000).toISOString().split('T')[0];

const STATUT_COLORS = { Brouillon: '#94a3b8', Envoyé: '#3b82f6', Accepté: '#22c55e', Refusé: '#ef4444', Expiré: '#f59e0b' };

const CATALOGUE = [
  { categorie: 'Diagnostic & Stratégie data', items: [
    { description: 'Audit de maturité data — Diagnostic & recommandations', unite: 'forfait', prix: 850, note: 'Livrable : rapport complet + feuille de route priorisée' },
    { description: 'Cartographie des flux de données (data mapping)', unite: 'forfait', prix: 1200, note: 'Inventaire des sources, flux, silos et dépendances' },
    { description: 'Stratégie data & définition de la feuille de route', unite: 'forfait', prix: 1800, note: 'Vision cible, quick wins, plan de mise en œuvre sur 12 mois' },
    { description: 'Atelier de cadrage — Définition du besoin', unite: 'jour', prix: 335, note: 'Préparation + animation + compte-rendu inclus' },
  ]},
  { categorie: 'Data Engineering & Infrastructure', items: [
    { description: 'Mise en place d\'un pipeline de données — Essentiel (1 source, chargement quotidien)', unite: 'forfait', prix: 2800, note: 'Livraison ~3 semaines. Monitoring de base inclus.' },
    { description: 'Mise en place d\'un pipeline de données — Avancé (multi-sources, transformations, alertes)', unite: 'forfait', prix: 6500, note: 'Livraison ~6 semaines. Documentation + recette incluses.' },
    { description: 'Déploiement d\'un entrepôt de données cloud (BigQuery, Snowflake ou équivalent)', unite: 'forfait', prix: 4800, note: 'Architecture, paramétrage, connexion aux sources, gouvernance' },
    { description: 'Connexion & normalisation de sources hétérogènes', unite: 'forfait', prix: 2200, note: 'CRM, ERP, tableurs, APIs — unification dans un modèle cible' },
    { description: 'Mise en qualité des données (profiling, règles, alertes)', unite: 'forfait', prix: 3200, note: 'Livrable : rapport qualité + règles automatisées opérationnelles' },
  ]},
  { categorie: 'Business Intelligence & Tableaux de bord', items: [
    { description: 'Tableau de bord décisionnel — Essentiel (1 source, jusqu\'à 6 vues métier)', unite: 'forfait', prix: 1150, note: 'Livraison ~2 semaines. Formation utilisateurs incluse (1h).' },
    { description: 'Tableau de bord décisionnel — Intermédiaire (multi-sources, KPIs avancés, alertes)', unite: 'forfait', prix: 2500, note: 'Livraison ~4 semaines. Recette + documentation incluses.' },
    { description: 'Tableau de bord décisionnel — Complet (ETL + multi-sources + rapports automatisés)', unite: 'forfait', prix: 4500, note: 'Livraison ~6 semaines. Maintenance 1 mois offerte.' },
    { description: 'Rapport automatisé — Envoi planifié (PDF / email / Slack)', unite: 'forfait', prix: 750, note: 'Fréquence configurable. Mise à jour auto des données.' },
    { description: 'Mise en place du self-service BI (outillage + formation)', unite: 'forfait', prix: 2200, note: 'Déploiement outil, modèle de données, formation équipe (demi-journée)' },
  ]},
  { categorie: 'Applications métier & Automatisation', items: [
    { description: 'Application métier sur mesure — Module standard', unite: 'forfait', prix: 4500, note: 'Formulaires, tableaux, exports. Livraison ~5 semaines.' },
    { description: 'Application métier sur mesure — Périmètre étendu (multi-modules, rôles, API)', unite: 'forfait', prix: 8500, note: 'Authentification, workflows, intégrations. Livraison ~10 semaines.' },
    { description: 'Automatisation d\'un processus métier (script, RPA ou no-code)', unite: 'forfait', prix: 1800, note: 'Gain de temps mesurable garanti contractuellement' },
    { description: 'Intégration API — Connexion entre deux systèmes', unite: 'forfait', prix: 1400, note: 'Authentification, mapping, gestion d\'erreurs, documentation' },
  ]},
  { categorie: 'RGPD & Conformité', items: [
    { description: 'Bilan de conformité RGPD initial', unite: 'forfait', prix: 1200, note: 'Livrable : rapport d\'écarts + plan d\'action priorisé' },
    { description: 'Rédaction & mise en place du registre des traitements', unite: 'forfait', prix: 900, note: 'Conforme aux exigences CNIL. Format éditable fourni.' },
    { description: 'Réalisation d\'une Analyse d\'Impact (AIPD / PIA)', unite: 'forfait', prix: 1600, note: 'Méthodologie CNIL. Livrable validé par DPO si requis.' },
    { description: 'Mise en conformité — Accompagnement complet (bilan + registre + AIPD)', unite: 'forfait', prix: 3200, note: 'Pack complet. Engagement de résultat. Suivi 3 mois inclus.' },
    { description: 'Formation RGPD — Sensibilisation équipe (jusqu\'à 12 personnes)', unite: 'forfait', prix: 650, note: 'Demi-journée. Support de formation remis aux participants.' },
  ]},
  { categorie: 'Formation & Accompagnement', items: [
    { description: 'Formation Data Literacy — Culture de la donnée en entreprise', unite: 'forfait', prix: 650, note: 'Demi-journée. Groupe jusqu\'à 15 personnes. Support inclus.' },
    { description: 'Formation outil sur mesure (Power BI, Looker Studio, Metabase…)', unite: 'forfait', prix: 1100, note: 'Journée complète. Exercices pratiques sur vos propres données.' },
    { description: 'Accompagnement prise en main — Suivi post-livraison', unite: 'heure', prix: 120, note: 'Sessions à la demande. Planifiable sur 3 mois.' },
    { description: 'Coaching data — Accompagnement d\'un responsable ou d\'une équipe', unite: 'jour', prix: 335, note: 'Transfert de compétences, bonnes pratiques, revues de travaux' },
  ]},
  { categorie: 'Maintenance & Support (MCO)', items: [
    { description: 'Maintenance Essentiel — Corrections & support réactif', unite: 'mois', prix: 225, note: 'Engagement 12 mois. Temps de réponse garanti 48h ouvrées.' },
    { description: 'Maintenance Évolutions — Corrections + nouvelles vues & intégrations', unite: 'mois', prix: 450, note: 'Engagement 12 mois. Jusqu\'à 4h d\'évolutions/mois incluses.' },
    { description: 'Support prioritaire — Hotline + interventions urgentes', unite: 'mois', prix: 750, note: 'Engagement 6 mois minimum. Temps de réponse garanti 4h.' },
    { description: 'Audit annuel de santé — Revue technique & recommandations', unite: 'forfait', prix: 600, note: 'Performance, sécurité, évolutions recommandées. Rapport inclus.' },
  ]},
  { categorie: 'Régie / TJM', items: [
    { description: 'Régie — Développement data & BI (analyste / développeur)', unite: 'jour', prix: 335, note: 'TJM standard. Devis sur mesure pour missions longues.' },
    { description: 'Régie — Architecture data & conseil senior', unite: 'jour', prix: 550, note: 'Profil senior / expertise architecture. Disponibilité à confirmer.' },
    { description: 'Régie — Data science & modélisation (ML, prédictif)', unite: 'jour', prix: 450, note: 'Études, modèles, déploiement. Sur devis selon périmètre.' },
  ]},
];

const Badge = ({ text, color }) => (
  <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{text}</span>
);

const is = { width: '100%', padding: '8px 10px', borderRadius: 6, background: '#0f1117', border: '1px solid #2a2d3e', color: '#e2e8f0', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' };
const inputStyle = { padding: '8px 12px', borderRadius: 8, background: '#1a1d2e', border: '1px solid #2a2d3e', color: '#e2e8f0', fontSize: 13, fontFamily: 'inherit', outline: 'none' };

// ─── PANEL DETAIL DEVIS ────────────────────────────────────────────────────
function DevisPanel({ devisId, onClose, onSave }) {
  const fetchWithAuth = useAuthFetch();
  const [devis, setDevis] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // { ok, msg }

  useEffect(() => {
    fetchWithAuth(`/api/devis/${devisId}`).then(r => r.json()).then(setDevis);
  }, [devisId]);

  const setStatut = async (statut) => {
    setSaving(true);
    await fetchWithAuth(`/api/devis/${devisId}`, { method: 'PUT', body: JSON.stringify({ statut }) });
    const updated = await fetchWithAuth(`/api/devis/${devisId}`).then(r => r.json());
    setDevis(updated); setSaving(false); onSave?.();
  };

  const convertToFacture = async () => {
    if (!window.confirm('Convertir ce devis en facture ?')) return;
    setSaving(true);
    await fetchWithAuth('/api/factures', {
      method: 'POST',
      body: JSON.stringify({
        client_id: devis.client_id, devis_id: devis.id, commercial_id: devis.commercial_id,
        titre: devis.titre, montant_ht: devis.montant_ht, taux_tva: devis.taux_tva,
        statut: 'Envoyée', date_emission: today(), date_echeance: inDays(30),
        lignes: devis.lignes,
      }),
    });
    setSaving(false); onSave?.(); onClose();
  };

  const del = async () => {
    if (!window.confirm('Supprimer ce devis ?')) return;
    await fetchWithAuth(`/api/devis/${devisId}`, { method: 'DELETE' });
    onClose(); onSave?.();
  };

  const envoyer = async () => {
    setSending(true); setSendResult(null);
    try {
      const res = await fetchWithAuth(`/api/devis/${devisId}/envoyer`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        setSendResult({ ok: true, msg: `Devis envoyé à ${data.email}` });
        const updated = await fetchWithAuth(`/api/devis/${devisId}`).then(r => r.json());
        setDevis(updated); onSave?.();
      } else {
        setSendResult({ ok: false, msg: data.error });
      }
    } catch {
      setSendResult({ ok: false, msg: 'Erreur de connexion' });
    } finally { setSending(false); }
  };

  if (!devis) return null;
  const tva = devis.montant_ht * (devis.taux_tva / 100);
  const ttc = devis.montant_ht + tva;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 }} />
      <div className="side-panel" style={{ width: 480 }}>
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #2a2d3e' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2, fontFamily: 'DM Mono, monospace' }}>{devis.numero}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>{devis.titre}</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 20 }}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Badge text={devis.statut} color={STATUT_COLORS[devis.statut] || '#64748b'} />
            <span style={{ fontSize: 12, color: '#64748b' }}>Émis le {fmtDate(devis.date_emission)}</span>
          </div>
        </div>
        <div className="modal-scroll" style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          <div style={{ background: '#41B07315', border: '1px solid #41B07330', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#94a3b8' }}>🏢 <span style={{ color: '#e2e8f0' }}>{devis.client_nom}</span></div>
          {devis.contact_nom && <div style={{ background: '#3b82f615', border: '1px solid #3b82f630', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#94a3b8' }}>🤝 <span style={{ color: '#e2e8f0' }}>{devis.contact_prenom} {devis.contact_nom}</span></div>}
          {devis.date_validite && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>Valide jusqu'au {fmtDate(devis.date_validite)}</div>}

          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2d3e' }}>
                {['Description', 'Unité', 'Qté', 'PU HT', 'Total HT'].map(h => (
                  <th key={h} style={{ padding: '6px 8px', textAlign: h === 'Description' ? 'left' : 'right', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(devis.lignes || []).map((l, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #2a2d3e22' }}>
                  <td style={{ padding: '8px', color: '#e2e8f0' }}>
                    {l.description}
                    {l.note && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{l.note}</div>}
                  </td>
                  <td style={{ padding: '8px', color: '#64748b', textAlign: 'right', fontSize: 11 }}>{l.unite || '—'}</td>
                  <td style={{ padding: '8px', color: '#94a3b8', textAlign: 'right' }}>{l.quantite}</td>
                  <td style={{ padding: '8px', color: '#94a3b8', textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{fmt(l.prix_unitaire)}</td>
                  <td style={{ padding: '8px', color: '#41B073', textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{fmt(l.quantite * l.prix_unitaire)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ background: '#0f111780', borderRadius: 8, padding: 14, marginBottom: 16 }}>
            {[['Total HT', fmt(devis.montant_ht), '#e2e8f0'], [`TVA (${devis.taux_tva}%)`, fmt(tva), '#94a3b8'], ['Total TTC', fmt(ttc), '#22c55e']].map(([l, v, c]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                <span style={{ color: '#64748b' }}>{l}</span>
                <span style={{ color: c, fontFamily: 'DM Mono, monospace', fontWeight: l === 'Total TTC' ? 700 : 400 }}>{v}</span>
              </div>
            ))}
          </div>

          {devis.notes && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', fontWeight: 600 }}>Notes</div>
              <div style={{ fontSize: 13, color: '#94a3b8', background: '#12141f', padding: 12, borderRadius: 6, whiteSpace: 'pre-wrap' }}>{devis.notes}</div>
            </div>
          )}
          {devis.conditions && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', fontWeight: 600 }}>Conditions</div>
              <div style={{ fontSize: 12, color: '#64748b', background: '#12141f', padding: 12, borderRadius: 6, whiteSpace: 'pre-wrap' }}>{devis.conditions}</div>
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {devis.statut === 'Brouillon' && <button onClick={() => setStatut('Envoyé')} disabled={saving} style={{ padding: '8px 14px', borderRadius: 8, background: '#3b82f620', border: '1px solid #3b82f640', color: '#3b82f6', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>📨 Marquer Envoyé</button>}
            {['Brouillon', 'Envoyé'].includes(devis.statut) && <button onClick={() => setStatut('Accepté')} disabled={saving} style={{ padding: '8px 14px', borderRadius: 8, background: '#22c55e20', border: '1px solid #22c55e40', color: '#22c55e', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>✅ Accepté</button>}
            {devis.statut === 'Envoyé' && <button onClick={() => setStatut('Refusé')} disabled={saving} style={{ padding: '8px 14px', borderRadius: 8, background: '#ef444420', border: '1px solid #ef444440', color: '#ef4444', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>❌ Refusé</button>}
            {devis.statut === 'Accepté' && <button onClick={convertToFacture} disabled={saving} style={{ padding: '8px 14px', borderRadius: 8, background: '#41B07320', border: '1px solid #41B07340', color: '#41B073', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>🧾 Convertir en facture</button>}
          </div>
          {sendResult && (
            <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, fontSize: 12, background: sendResult.ok ? '#10b98120' : '#ef444420', border: `1px solid ${sendResult.ok ? '#10b98140' : '#ef444440'}`, color: sendResult.ok ? '#10b981' : '#ef4444' }}>
              {sendResult.ok ? '✓' : '✕'} {sendResult.msg}
            </div>
          )}
          <div style={{ paddingTop: 12, borderTop: '1px solid #2a2d3e', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={envoyer} disabled={sending} style={{ padding: '8px 14px', borderRadius: 8, background: '#3b82f620', border: '1px solid #3b82f640', color: '#3b82f6', cursor: sending ? 'not-allowed' : 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
              {sending ? '⏳ Envoi...' : '📧 Envoyer au client'}
            </button>
            <button onClick={() => printDevis(devis)} style={{ padding: '8px 14px', borderRadius: 8, background: 'transparent', border: '1px solid #41B07340', color: '#41B073', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>📄 PDF</button>
            <button onClick={del} style={{ padding: '8px 14px', borderRadius: 8, background: 'transparent', border: '1px solid #ef444440', color: '#ef4444', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>🗑 Supprimer</button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── MODAL CRÉATION DEVIS ─────────────────────────────────────────────────
function CreationModal({ onClose, onSave }) {
  const fetchWithAuth = useAuthFetch();
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showCatalogue, setShowCatalogue] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    client_id: '',
    contact_id: '',
    titre: '',
    date_emission: today(),
    date_validite: inDays(30),
    taux_tva: 20,
    statut: 'Brouillon',
    notes: '',
    conditions: 'Devis valable 30 jours à compter de la date d\'émission.\nAcompte de 40 % à la commande, solde à la livraison.\nPropriété intellectuelle transférée après règlement intégral.',
  });

  const [lignes, setLignes] = useState([
    { description: '', unite: 'forfait', quantite: 1, prix_unitaire: '', note: '' },
  ]);

  useEffect(() => {
    fetchWithAuth('/api/clients?limit=999&statut=Client actif').then(r => r.json()).then(d => setClients(d.data || []));
  }, []);

  useEffect(() => {
    if (!form.client_id) { setContacts([]); return; }
    fetchWithAuth(`/api/contacts?client_id=${form.client_id}&limit=50`).then(r => r.json()).then(d => setContacts(d.data || []));
    setForm(f => ({ ...f, contact_id: '' }));
  }, [form.client_id]);

  const totalHT = lignes.reduce((s, l) => s + (parseFloat(l.quantite) || 0) * (parseFloat(l.prix_unitaire) || 0), 0);
  const tva = totalHT * (form.taux_tva / 100);
  const ttc = totalHT + tva;

  const setLigne = (i, key, val) => setLignes(ls => ls.map((l, idx) => idx === i ? { ...l, [key]: val } : l));
  const addLigne = () => setLignes(ls => [...ls, { description: '', unite: 'forfait', quantite: 1, prix_unitaire: '', note: '' }]);
  const removeLigne = (i) => setLignes(ls => ls.filter((_, idx) => idx !== i));

  const addFromCatalogue = (item) => {
    setLignes(ls => [...ls, { description: item.description, unite: item.unite, quantite: 1, prix_unitaire: item.prix, note: item.note || '' }]);
    setShowCatalogue(false);
  };

  const save = async (statut) => {
    const e = {};
    if (!form.client_id) e.client_id = true;
    if (!form.titre.trim()) e.titre = true;
    if (lignes.length === 0 || lignes.every(l => !l.description.trim())) e.lignes = true;
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      await fetchWithAuth('/api/devis', {
        method: 'POST',
        body: JSON.stringify({ ...form, statut, montant_ht: totalHT, lignes }),
      });
      onSave();
      onClose();
    } finally { setSaving(false); }
  };

  const UNITES = ['forfait', 'jour', 'mois', 'heure', 'unité'];

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200 }} />
      <div style={{ position: 'fixed', inset: '20px', background: '#12141f', border: '1px solid #2a2d3e', borderRadius: 16, zIndex: 201, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #2a2d3e', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>Nouveau devis</h2>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>LA DONNÉE — Solutions data sur mesure</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 22 }}>✕</button>
        </div>

        {/* Body scrollable */}
        <div className="modal-scroll" style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          <div className="form-grid" style={{ gap: 24 }}>

            {/* Colonne gauche — infos */}
            <div>
              <div style={{ fontSize: 12, color: '#41B073', fontWeight: 700, textTransform: 'uppercase', marginBottom: 14, letterSpacing: 1 }}>Informations générales</div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 5, fontWeight: 500 }}>Client *</label>
                <select value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} style={{ ...is, borderColor: errors.client_id ? '#ef4444' : '#2a2d3e', cursor: 'pointer' }}>
                  <option value="">— Sélectionner un client —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.nom} ({c.type})</option>)}
                </select>
                {errors.client_id && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 3 }}>Client requis</div>}
              </div>

              {contacts.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 5, fontWeight: 500 }}>Contact</label>
                  <select value={form.contact_id} onChange={e => setForm(f => ({ ...f, contact_id: e.target.value }))} style={{ ...is, cursor: 'pointer' }}>
                    <option value="">— Aucun contact spécifique —</option>
                    {contacts.map(c => <option key={c.id} value={c.id}>{c.prenom} {c.nom} — {c.poste}</option>)}
                  </select>
                </div>
              )}

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 5, fontWeight: 500 }}>Intitulé du devis *</label>
                <input value={form.titre} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))} placeholder="ex : Dashboard production Mécanique Picard" style={{ ...is, borderColor: errors.titre ? '#ef4444' : '#2a2d3e' }} />
                {errors.titre && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 3 }}>Intitulé requis</div>}
              </div>

              <div className="form-grid" style={{ marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 5, fontWeight: 500 }}>Date d'émission</label>
                  <input type="date" value={form.date_emission} onChange={e => setForm(f => ({ ...f, date_emission: e.target.value }))} style={is} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 5, fontWeight: 500 }}>Date de validité</label>
                  <input type="date" value={form.date_validite} onChange={e => setForm(f => ({ ...f, date_validite: e.target.value }))} style={is} />
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 5, fontWeight: 500 }}>TVA (%)</label>
                <select value={form.taux_tva} onChange={e => setForm(f => ({ ...f, taux_tva: Number(e.target.value) }))} style={{ ...is, cursor: 'pointer' }}>
                  <option value={20}>20 % — TVA standard</option>
                  <option value={10}>10 %</option>
                  <option value={0}>0 % — Exonéré</option>
                </select>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 5, fontWeight: 500 }}>Notes pour le client</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Message d'accompagnement, contexte, détails spécifiques..." style={{ ...is, height: 80, resize: 'vertical' }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 5, fontWeight: 500 }}>Conditions générales</label>
                <textarea value={form.conditions} onChange={e => setForm(f => ({ ...f, conditions: e.target.value }))} style={{ ...is, height: 90, resize: 'vertical', fontSize: 12 }} />
              </div>
            </div>

            {/* Colonne droite — lignes */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: '#41B073', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Lignes de devis</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setShowCatalogue(s => !s)} style={{ padding: '5px 12px', borderRadius: 6, background: showCatalogue ? '#41B07330' : '#41B07315', border: '1px solid #41B07340', color: '#41B073', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', fontWeight: 600 }}>
                    📋 Catalogue
                  </button>
                  <button onClick={addLigne} style={{ padding: '5px 12px', borderRadius: 6, background: 'transparent', border: '1px solid #2a2d3e', color: '#94a3b8', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
                    + Ligne vide
                  </button>
                </div>
              </div>

              {/* Catalogue déroulant */}
              {showCatalogue && (
                <div className="modal-scroll" style={{ background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 10, padding: 16, marginBottom: 16, maxHeight: 280, overflowY: 'auto' }}>
                  {CATALOGUE.map(cat => (
                    <div key={cat.categorie} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 10, color: '#41B073', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{cat.categorie}</div>
                      {cat.items.map((item, i) => (
                        <div key={i} onClick={() => addFromCatalogue(item)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', borderRadius: 6, cursor: 'pointer', marginBottom: 3, background: '#0f111780' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#41B07315'}
                          onMouseLeave={e => e.currentTarget.style.background = '#0f111780'}>
                          <div>
                            <div style={{ fontSize: 12, color: '#e2e8f0' }}>{item.description}</div>
                            {item.note && <div style={{ fontSize: 11, color: '#64748b' }}>{item.note}</div>}
                          </div>
                          <div style={{ fontSize: 12, color: '#41B073', fontFamily: 'DM Mono, monospace', fontWeight: 600, whiteSpace: 'nowrap', marginLeft: 12 }}>
                            {fmt(item.prix)} / {item.unite}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {errors.lignes && <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 10 }}>Au moins une ligne avec une description est requise.</div>}

              {/* En-tête lignes */}
              <div style={{ display: 'grid', gridTemplateColumns: '3fr 80px 60px 90px 30px', gap: 6, marginBottom: 6 }}>
                {['Description / Prestation', 'Unité', 'Qté', 'PU HT (€)', ''].map((h, i) => (
                  <div key={i} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', padding: '0 4px' }}>{h}</div>
                ))}
              </div>

              {/* Lignes */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {lignes.map((l, i) => (
                  <div key={i}>
                    <div style={{ display: 'grid', gridTemplateColumns: '3fr 80px 60px 90px 30px', gap: 6, alignItems: 'start' }}>
                      <input value={l.description} onChange={e => setLigne(i, 'description', e.target.value)} placeholder="Description de la prestation" style={{ ...is, fontSize: 12 }} />
                      <select value={l.unite} onChange={e => setLigne(i, 'unite', e.target.value)} style={{ ...is, fontSize: 12, cursor: 'pointer', padding: '8px 6px' }}>
                        {UNITES.map(u => <option key={u}>{u}</option>)}
                      </select>
                      <input type="number" min="0.5" step="0.5" value={l.quantite} onChange={e => setLigne(i, 'quantite', e.target.value)} style={{ ...is, fontSize: 12 }} />
                      <input type="number" min="0" value={l.prix_unitaire} onChange={e => setLigne(i, 'prix_unitaire', e.target.value)} placeholder="0" style={{ ...is, fontSize: 12 }} />
                      <button onClick={() => removeLigne(i)} disabled={lignes.length === 1} style={{ background: 'none', border: 'none', color: lignes.length === 1 ? '#2a2d3e' : '#ef444480', cursor: lignes.length === 1 ? 'default' : 'pointer', fontSize: 16, padding: '6px 0' }}>✕</button>
                    </div>
                    <input value={l.note} onChange={e => setLigne(i, 'note', e.target.value)} placeholder="Note optionnelle (ex : délai, précision technique...)" style={{ ...is, fontSize: 11, marginTop: 4, color: '#64748b' }} />
                    {i < lignes.length - 1 && <div style={{ borderBottom: '1px solid #2a2d3e22', marginTop: 8 }} />}
                  </div>
                ))}
              </div>

              {/* Totaux */}
              <div style={{ background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 10, padding: 16, marginTop: 20 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: '#64748b' }}>Total HT</span>
                    <span style={{ color: '#e2e8f0', fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{fmt(totalHT)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: '#64748b' }}>TVA ({form.taux_tva} %)</span>
                    <span style={{ color: '#94a3b8', fontFamily: 'DM Mono, monospace' }}>{fmt(tva)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, paddingTop: 8, borderTop: '1px solid #2a2d3e', marginTop: 4 }}>
                    <span style={{ color: '#e2e8f0', fontWeight: 700 }}>Total TTC</span>
                    <span style={{ color: '#22c55e', fontFamily: 'DM Mono, monospace', fontWeight: 700 }}>{fmt(ttc)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer — actions */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #2a2d3e', display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0, background: '#12141f' }}>
          <button onClick={onClose} style={{ padding: '10px 20px', borderRadius: 8, background: 'transparent', border: '1px solid #2a2d3e', color: '#94a3b8', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>Annuler</button>
          <button onClick={() => save('Brouillon')} disabled={saving} style={{ padding: '10px 20px', borderRadius: 8, background: '#1a1d2e', border: '1px solid #41B07340', color: '#41B073', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', fontWeight: 600 }}>
            {saving ? '...' : '💾 Enregistrer brouillon'}
          </button>
          <button onClick={() => save('Envoyé')} disabled={saving} style={{ padding: '10px 24px', borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', fontWeight: 600 }}>
            {saving ? '...' : '📨 Enregistrer & marquer envoyé'}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────────────────────────────────
export default function DevisPage() {
  const fetchWithAuth = useAuthFetch();
  const location = useLocation();
  const { search, setSearch, page, setPage, limit, setLimit, sortField, sortDir, toggleSort, filters, setFilter, buildQuery } = useFilters({ sortField: 'created_at', sortDir: 'desc' });
  const [devisList, setDevisList] = useState([]);
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(location.state?.devisId || null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, all] = await Promise.all([
        fetchWithAuth(`/api/devis?${buildQuery()}`).then(r => r.json()),
        fetchWithAuth('/api/devis?limit=999').then(r => r.json()),
      ]);
      setDevisList(res.data || []); setTotal(res.total || 0);
      const counts = { total: 0, Brouillon: 0, Envoyé: 0, Accepté: 0, montantAccepte: 0 };
      (all.data || []).forEach(d => {
        counts.total++; if (counts[d.statut] !== undefined) counts[d.statut]++;
        if (d.statut === 'Accepté') counts.montantAccepte += d.montant_ht || 0;
      });
      setKpis(counts);
    } finally { setLoading(false); }
  }, [search, page, limit, sortField, sortDir, JSON.stringify(filters)]);

  useEffect(() => { load(); }, [load]);

  const SortTh = ({ field, children }) => (
    <th onClick={() => toggleSort(field)} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}>
      {children} {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );
  const totalPages = Math.ceil(total / limit);

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>Devis</h1>
        <button onClick={() => setShowCreate(true)} style={{ padding: '9px 18px', borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>+ Nouveau devis</button>
      </div>

      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        <KPICard title="Total" value={kpis.total || 0} color="#41B073" icon="📄" onClick={() => setFilter('statut', '')} />
        <KPICard title="Brouillons" value={kpis.Brouillon || 0} color="#94a3b8" onClick={() => setFilter('statut', 'Brouillon')} />
        <KPICard title="Envoyés" value={kpis.Envoyé || 0} color="#3b82f6" onClick={() => setFilter('statut', 'Envoyé')} />
        <KPICard title="Acceptés" value={kpis.Accepté || 0} color="#22c55e" onClick={() => setFilter('statut', 'Accepté')} />
        <KPICard title="Montant accepté" value={new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(kpis.montantAccepte || 0)} color="#22c55e" />
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Rechercher..." style={{ ...inputStyle, minWidth: 200 }} />
        <select value={filters.statut || ''} onChange={e => setFilter('statut', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
          <option value="">Tous statuts</option>
          {['Brouillon', 'Envoyé', 'Accepté', 'Refusé', 'Expiré'].map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} style={{ ...inputStyle, cursor: 'pointer' }}>
          {[25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
        </select>
      </div>

      <div style={{ background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2d3e' }}>
                <SortTh field="numero">Numéro</SortTh>
                <SortTh field="titre">Intitulé</SortTh>
                <th style={{ padding: '10px 14px', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', textAlign: 'left' }}>Client</th>
                <SortTh field="montant_ht">HT</SortTh>
                <th style={{ padding: '10px 14px', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', textAlign: 'left' }}>TTC</th>
                <SortTh field="statut">Statut</SortTh>
                <SortTh field="date_emission">Émis</SortTh>
                <SortTh field="date_validite">Valide jusqu'au</SortTh>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={8} style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Chargement...</td></tr>
                : devisList.length === 0 ? <tr><td colSpan={8} style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Aucun devis</td></tr>
                : devisList.map((d, i) => (
                  <tr key={d.id} onClick={() => setSelectedId(d.id)} style={{ borderBottom: '1px solid #2a2d3e', cursor: 'pointer', background: i % 2 === 0 ? 'transparent' : '#ffffff05' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#41B07315'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : '#ffffff05'}>
                    <td style={{ padding: '11px 14px', fontSize: 12, color: '#64748b', fontFamily: 'DM Mono, monospace' }}>{d.numero}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: '#e2e8f0', fontWeight: 500 }}>{d.titre}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{d.client_nom}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: '#41B073', fontFamily: 'DM Mono, monospace' }}>{fmt(d.montant_ht)}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8', fontFamily: 'DM Mono, monospace' }}>{fmt(d.montant_ht * (1 + d.taux_tva / 100))}</td>
                    <td style={{ padding: '11px 14px' }}><Badge text={d.statut} color={STATUT_COLORS[d.statut] || '#64748b'} /></td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{fmtDate(d.date_emission)}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{fmtDate(d.date_validite)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #2a2d3e' }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>{total} devis</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '5px 12px', borderRadius: 6, background: 'transparent', border: '1px solid #2a2d3e', color: page === 1 ? '#2a2d3e' : '#94a3b8', cursor: page === 1 ? 'default' : 'pointer', fontSize: 13 }}>←</button>
            <span style={{ padding: '5px 12px', fontSize: 13, color: '#94a3b8' }}>Page {page} / {totalPages || 1}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ padding: '5px 12px', borderRadius: 6, background: 'transparent', border: '1px solid #2a2d3e', color: page >= totalPages ? '#2a2d3e' : '#94a3b8', cursor: page >= totalPages ? 'default' : 'pointer', fontSize: 13 }}>→</button>
          </div>
        </div>
      </div>

      {selectedId && <DevisPanel devisId={selectedId} onClose={() => setSelectedId(null)} onSave={load} />}
      {showCreate && <CreationModal onClose={() => setShowCreate(false)} onSave={load} />}
    </div>
  );
}
